// "Stát se community adminem" — settings panel for tier 3+ users to
// apply for an admin role over one or more cities. Approval is a soft
// hand-shake; the actual admin login (email+password) is set up
// out-of-band by the superadmin via the existing invite flow.

import { use, useEffect, useState, type FC } from "react";
import { evolu } from "../../evolu";
import { CITIES } from "../../data/cities";
import {
  fetchAdminApplication, postAdminApplication,
  requestAdminChallenge, loginAdminAsOwner,
  type AdminApplicationStatus,
} from "../../api";
import { fetchTierState, type TierState } from "../../lib/tierSystem";
import { deriveOwnerKeypair } from "../../lib/ownerCrypto";
import { LS } from "../../lib/storageKeys";

export const AdminApplyPanel: FC = () => {
  const owner = use(evolu.appOwner);
  const ownerId = owner.id as string;

  const [tier, setTier] = useState<TierState | null>(null);
  const [existing, setExisting] = useState<AdminApplicationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [pickedCities, setPickedCities] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchTierState(ownerId), fetchAdminApplication(ownerId)])
      .then(([t, a]) => {
        if (cancelled) return;
        setTier(t);
        setExisting(a);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ownerId]);

  if (loading) return <p className="small muted">Načítám…</p>;

  // Tier gate
  if (!tier || tier.tier < 3) {
    return (
      <section className="card">
        <h2>Stát se community adminem</h2>
        <p className="hint">
          Admin role je otevřená pro tier 3 a výš. Aktuálně jsi <strong>Tier {tier?.tier ?? 1}</strong>.
          Postup začíná v Profile → Tvoje úroveň.
        </p>
      </section>
    );
  }

  // Existing application — show status
  if (existing && existing.status === "pending") {
    return (
      <section className="card">
        <h2>Stát se community adminem</h2>
        <p className="hint">
          Tvoje žádost byla odeslána <strong>{new Date(existing.appliedAt).toLocaleString("cs-CZ")}</strong>{" "}
          a čeká na schválení od superadmina.
        </p>
        <p className="small muted">
          Požadovaná města: <strong>{existing.citiesCsv.split(",").map((s) => s.trim()).join(", ")}</strong>
        </p>
        {existing.message && (
          <p className="small">Tvoje zpráva: <em>{existing.message}</em></p>
        )}
      </section>
    );
  }
  if (existing && existing.status === "approved") {
    return <ApprovedPanel ownerId={ownerId} mnemonic={owner.mnemonic as string | null} />;
  }
  if (existing && existing.status === "rejected") {
    return (
      <section className="card">
        <h2>Stát se community adminem</h2>
        <p style={{ color: "var(--danger)" }}>Tvoje žádost byla zamítnuta.</p>
        {existing.rejectReason && (
          <p className="small muted">Důvod: <em>{existing.rejectReason}</em></p>
        )}
        <p className="small muted">
          Můžeš podat novou žádost níže.
        </p>
        {/* Fall through to the form — rejected applications can be retried. */}
        <ApplyForm
          ownerId={ownerId}
          pickedCities={pickedCities}
          setPickedCities={setPickedCities}
          message={message}
          setMessage={setMessage}
          submitting={submitting}
          err={err}
          submitted={submitted}
          onSubmit={async () => {
            setErr(null);
            if (pickedCities.length === 0) { setErr("Vyber alespoň jedno město."); return; }
            // Commit the ed25519 pubkey derived from the user's mnemonic
            // alongside the application. Approval then mints an admin_user
            // bound to this owner_id without ever asking for a password.
            const pubkeyHex = owner.mnemonic ? deriveOwnerKeypair(owner.mnemonic).pubkeyHex : undefined;
            setSubmitting(true);
            const r = await postAdminApplication(ownerId, pickedCities.join(","), message, pubkeyHex);
            setSubmitting(false);
            if (!r.ok) setErr(r.error);
            else setSubmitted(true);
          }}
        />
      </section>
    );
  }

  if (submitted) {
    return (
      <section className="card">
        <h2>Stát se community adminem</h2>
        <p style={{ color: "var(--ok)" }}>
          ✓ Žádost odeslána. Superadmin ji zkontroluje a ozve se ti.
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>Stát se community adminem</h2>
      <p className="hint">
        Jsi <strong>Tier {tier.tier}</strong> a chceš pomáhat vést komunitu? Vyber, kterým
        městům bys chtěl(a) administrovat (přidávat akce, posílat broadcasty), a stručně
        napiš proč. Superadmin žádost zkontroluje a ozve se ti s pozvánkou pro vytvoření
        admin účtu.
      </p>
      <ApplyForm
        ownerId={ownerId}
        pickedCities={pickedCities}
        setPickedCities={setPickedCities}
        message={message}
        setMessage={setMessage}
        submitting={submitting}
        err={err}
        submitted={submitted}
        onSubmit={async () => {
          setErr(null);
          if (pickedCities.length === 0) { setErr("Vyber alespoň jedno město."); return; }
          setSubmitting(true);
          const r = await postAdminApplication(ownerId, pickedCities.join(","), message);
          setSubmitting(false);
          if (!r.ok) setErr(r.error);
          else setSubmitted(true);
        }}
      />
    </section>
  );
};

// Shown after the application is approved. The "Otevřít admin panel"
// button does the challenge → sign → session dance in one click and
// then opens /admin. The session token lands in the same localStorage
// key AdminApp reads, so it picks up the admin straight away.
const ApprovedPanel: FC<{ ownerId: string; mnemonic: string | null }> = ({ ownerId, mnemonic }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const openAdmin = async () => {
    if (!mnemonic) {
      setErr("Chybí mnemonic — bez něj nelze podepsat výzvu.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const nonce = await requestAdminChallenge(ownerId);
      if (!nonce) { setErr("Backend nevydal výzvu."); return; }
      const kp = deriveOwnerKeypair(mnemonic);
      const sig = kp.sign(nonce);
      const r = await loginAdminAsOwner(ownerId, nonce, sig);
      if (!r.ok) { setErr(r.error); return; }
      try { localStorage.setItem(LS.AdminSession, r.token); } catch { /* private mode */ }
      window.location.href = "/admin";
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Stát se community adminem</h2>
      <p style={{ color: "var(--ok)" }}>
        ✓ Tvoje žádost byla schválena. Admin přístup je napojený na tvoji
        chat identitu — nemusíš si nastavovat druhé heslo.
      </p>
      <p className="small muted">
        Klikni níže: aplikace podepíše výzvu klíčem odvozeným z tvojí 24-slovní
        fráze a otevře admin panel. Nikdy se neposílá samotná fráze, jen podpis.
      </p>
      {err && <p className="error small">{err}</p>}
      <button className="btn btn-primary mt-md" onClick={openAdmin} disabled={busy}>
        {busy ? "Otevírám…" : "Otevřít admin panel →"}
      </button>
    </section>
  );
};

const ApplyForm: FC<{
  ownerId: string;
  pickedCities: string[];
  setPickedCities: (cs: string[]) => void;
  message: string;
  setMessage: (s: string) => void;
  submitting: boolean;
  err: string | null;
  submitted: boolean;
  onSubmit: () => void;
}> = ({ pickedCities, setPickedCities, message, setMessage, submitting, err, onSubmit }) => {
  const toggle = (slug: string) => {
    if (pickedCities.includes(slug)) setPickedCities(pickedCities.filter((s) => s !== slug));
    else setPickedCities([...pickedCities, slug]);
  };
  return (
    <div className="form mt-md">
      <div className="field">
        <label>Města ({pickedCities.length}/10)</label>
        <div className="chip-row">
          {CITIES.map((c) => (
            <button
              key={c.slug}
              type="button"
              className={`chip selectable ${pickedCities.includes(c.slug) ? "active" : ""}`}
              onClick={() => toggle(c.slug)}
              disabled={!pickedCities.includes(c.slug) && pickedCities.length >= 10}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <label>Krátká zpráva pro superadmina (volitelné)</label>
        <textarea
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
          placeholder="Např. Pořádám měsíční meetupy v Brně, mám spojení s místní komunitou…"
        />
        <p className="small muted" style={{ marginTop: "0.3rem" }}>{message.length}/1000</p>
      </div>
      {err && <p className="error">{err}</p>}
      <button className="btn btn-primary" onClick={onSubmit} disabled={submitting || pickedCities.length === 0}>
        {submitting ? "Odesílám…" : "Odeslat žádost"}
      </button>
    </div>
  );
};
